import { z } from "zod";

const EMPLOYMENT_TYPES = [
	"fixedTerm",
	"contractor",
	"probation",
	"permanent",
	"subcontractor",
] as const;
const PENSION_TYPES = ["itp1", "itp2", "notWhiteCollar"] as const;
const UNION_TYPES = ["noAfa", "blueCollar", "whiteCollar"] as const;

// Schema for monthly guaranteed hours (12 months)
// Generate schema dynamically for all 12 months to avoid repetition
const monthlyHourSchema = z
	.number({ message: "NUMBER_REQUIRED" })
	.int("MUST_BE_INTEGER")
	.min(0, "MIN_0")
	.max(180, "MAX_180");

const guaranteedHoursByMonthSchema = z.object(
	Object.fromEntries(Array.from({ length: 12 }, (_, i) => [i + 1, monthlyHourSchema]))
);

export const CollectiveAgreementApiSchema = z
	.object({
		employmentType: z.enum(EMPLOYMENT_TYPES, {
			message: "EMPLOYMENT_TYPE_INVALID",
		}),

		guaranteeSalary: z
			.number({
				message: "NUMBER_REQUIRED",
			})
			.min(0, "MIN_0")
			.optional(),

		guaranteedHoursPerMonth: z
			.number({
				message: "NUMBER_REQUIRED",
			})
			.int("MUST_BE_INTEGER")
			.min(0, "MIN_0")
			.optional(),

		guaranteedHoursByMonth: guaranteedHoursByMonthSchema.optional(),

		internalDescription: z.string().nullable().optional(),

		isGflControlled: z.boolean({
			message: "FIELD_IS_REQUIRED",
		}),

		name: z.string().min(1, "FIELD_IS_REQUIRED"),

		pensionType: z.enum(PENSION_TYPES, {
			message: "PENSION_TYPE_INVALID",
		}),

		productionUnit: z.string().nullable().optional(),

		salaryProgramCategory: z.string().nullable().optional(),

		salaryStatistics: z.object({
			wageType: z.string().min(1, "FIELD_IS_REQUIRED"),
			agreementCode: z.string().nullable().optional(),
			federalNumber: z.string().nullable().optional(),
			occupationCode: z.string().nullable().optional(),
			personnelCategory: z.string().nullable().optional(),
			unionNumber: z.string().nullable().optional(),
			workTimeCode: z.string().nullable().optional(),
			workplaceNumberScb: z.string().nullable().optional(),
			workplaceNumberSn: z.string().nullable().optional(),
		}),

		unionType: z.enum(UNION_TYPES, {
			message: "UNION_TYPE_INVALID",
		}),

		vacationSalaryPercent: z
			.number({
				message: "NUMBER_REQUIRED",
			})
			.min(0, "MIN_0")
			.max(100, "MAX_100"),

		// Additional fields
		itpCostCenter: z.string().nullable().optional(),
		itpPlanId: z.string().nullable().optional(),
	})
	.refine(
		(data) => {
			if (data.employmentType === "fixedTerm") {
				return data.guaranteedHoursByMonth !== undefined;
			}
			return true;
		},
		{
			message: "FIELD_IS_REQUIRED",
			path: ["guaranteedHoursByMonth"],
		}
	);

export type CollectiveAgreementFormData = z.infer<typeof CollectiveAgreementApiSchema>;
